import { Component, HostListener, OnInit, inject, isDevMode } from '@angular/core';
import { SocketService } from './services/socket.service';
import { Meta, Title } from '@angular/platform-browser';
import { environment as env } from '../environments/environment';
import { TranslocoService, getBrowserLang } from '@jsverse/transloco';
import * as storageHelper from '../app/modules/core/helpers/storage-helper';
import { PwaCheckUpdateService } from './modules/core/services/pwa-check-update.service';
import { PwaLogUpdateService } from './modules/core/services/pwa-log-update.service';
import { PwaRecoverService } from './modules/core/services/pwa-recover.service';
import { LogLevel, LoggingService } from './services/logging.service';
import { MatDialog } from '@angular/material/dialog';
import { LoggingSetupDialogComponent } from './components/logging-setup-dialog/logging-setup-dialog.component';
import { PwaPromptUpdateService } from './modules/core/services/pwa-prompt-update.service';
import { PwaPromptInstallService } from './modules/core/services/pwa-prompt-install.service';
import { forkJoin } from 'rxjs';
import { AppService } from './services/app.service';
import { AppAnalyticsService } from './services/app-analytics.service';
import { Store } from '@ngrx/store';
import * as fromApp from './modules/core/store/app.state';
import * as BrandActions from './modules/core/store/actions/brand.actions';
import * as AuthActions from './modules/core/store/actions/auth.actions';
import { take } from 'rxjs/operators';


@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss',
    standalone: false
})
export class AppComponent implements OnInit {
  static readonly COMPONENT_NAME = 'AppComponent';

  loginHost: string = '';
  defaultOrganizationDetail: any;
  organizationDetails: any;
  loaded: boolean = false;

  orgId = 0;

  socketService = inject(SocketService);
  titleService = inject(Title);
  translocoService = inject(TranslocoService);
  pwaCheckUpdateService = inject(PwaCheckUpdateService);
  pwaLogUpdateService = inject(PwaLogUpdateService);
  pwaPromptUpdateService = inject(PwaPromptUpdateService);
  pwaRecoverService = inject(PwaRecoverService);
  dialog = inject(MatDialog);
  loggingService = inject(LoggingService);
  pwaPromptInstallService = inject(PwaPromptInstallService);
  appAnalyticsService = inject(AppAnalyticsService);
  canSaveLanguageChange = false;
  showLoggingSetupButton = false;

  themeData: any = {
    logos: {
      "banner": {
        "img": "/organizationLogos/ffecf755-3f37-4548-aa19-4be1b0144610.jpg",
        "loading": false
      },
      "logo3": {
        "img": "/organizationLogos/2bd14798-a7b5-425b-83eb-c022f589b67d.png",
        "loading": false
      },
      "logo2": {
        "img": "/organizationLogos/724bb680-8ddf-4cbf-a5c9-a79d80afea71.png",
        "loading": false
      },
      "logo1": {
        "img": "/organizationLogos/875bb0c8-13d6-4092-a808-ed6dddab07bc.png",
        "loading": false
      }
      // logo2: "assets/img/senses-logo.png",
    },
    bgAndFontColor: {
      "header": {
        "text": "#ffffff",
        "font": 'Roboto, "Helvetica Neue", sans-serif',
        "bold": false,
        "bg": [
          "#e26063",
          "#c32124"
        ]
      },
      "dashboard": {
        "text": "#ffffff",
        "font": 'Roboto, "Helvetica Neue", sans-serif',
        "bold": false,
        "inBox": true,
        "bg": [
          "#c32124"
        ]
      },
      "sidebar": {
        "text": "#ffffff",
        "font": 'got_med',
        "bold": false,
        "activeBg": [
          "#e26063"
        ],
        "bg": [
          "#c32124"
        ]
      },
      "default": {
        "font": "OCR A Std, monospace",
        "btnBorder": false,
        "btnType": "",
        "text": "#c32124",
        "primary": "#c32124",
        "heading": "#c32124",
        "icon": "#c32124",
        "bg": [
          "#f1eaeb"
        ],
        "primaryBtn": {
          "text": "#ffffff",
          "bg": [
            "#c32124"
          ],
        },
        "secondaryBtn": {
          "text": "#ffffff",
          "bg": [
            "#e26063"
          ],
        },
        "cancelBtn": {
          "text": "#c32124",
          "bg": [
            "#ffffff"
          ],
        },
        "infoBtn": {
          "text": "#f44239",
          "bg": [
            "#f5fb4d"
          ],
        },
        "warningBtn": {
          "text": "#ffffff",
          "bg": [
            "#f44239"
          ],
        },
        "successBtn": {
          "text": "#ffffff",
          "bg": [
            "#36834f"
          ],
        },
      },
      "notification": {
        "text": "#ffffff",
        "heading": "#ffffff",
        "font": 'Roboto, "Helvetica Neue", sans-serif',
        "bold": false,
        "bg": [
          "#c32124"
        ]
      },
      "account": {
        "text": "#ffffff",
        "font": 'Roboto, "Helvetica Neue", sans-serif',
        "bold": false,
        "bg": [
          "#c32124"
        ]
      },
      "login": {
        "desktop": {
          "page": '1',
          "btnType": "",
          "font": "",
          "radius": true,
          "circleFlag": false,
          "toAll": false,
          "btnText": "#ffffff",
          "heading": "#ffffff",
          "text": "#000000",
          "bg": [
            "#e26063",
            "#c32124"
          ],
          "btnBg": [
            "#e26063",
            "#c32124"
          ]
        },
        "mobile": {
          "page": '1',
          "btnType": "",
          "font": "",
          "radius": true,
          "circleFlag": false,
          "toAll": false,
          "btnText": "#ffffff",
          "heading": "#ffffff",
          "text": "#000000",
          "bg": [
            "#e26063",
            "#c32124"
          ],
          "btnBg": [
            "#e26063",
            "#c32124"
          ]
        },
      }
    }
  }

  tenantAppTheme: any = {};

  constructor(
    public appService: AppService,
    private metaService: Meta,
    private store: Store<fromApp.AppState>,
  ) { }

  ngOnInit(): void {
    this.appService.setThemeConfigLoading(true);
    this.titleService.setTitle(`Hthai Clienteling (v${env.appVersion})`);

    this.loginHost = window.location.host;

    let defaultOrganizationDetails$ = this.appService.getOrganisationDetailsForTheme(1, 'id');
    let organizationDetails$ = this.appService.getOrganisationDetailsForTheme(this.loginHost, 'domain');
    forkJoin([defaultOrganizationDetails$, organizationDetails$]).subscribe(data => {
      console.log('Theme Data:', data);
      this.defaultOrganizationDetail = data[0].data.organisationDetails;
      console.log("=== Default Organisation Detail ===", this.defaultOrganizationDetail);
      this.orgId = this.defaultOrganizationDetail.id;
      if (this.defaultOrganizationDetail.themeConfig && this.defaultOrganizationDetail.themeConfig != '') {
        this.themeData = this.defaultOrganizationDetail.themeConfig;
      }

      if (this.defaultOrganizationDetail.themeData && this.defaultOrganizationDetail.themeData != '') {
        this.tenantAppTheme = this.defaultOrganizationDetail.themeData;
      }

      this.organizationDetails = data[1].data.organisationDetails;
      console.log("=== Organisation Detail ===", this.organizationDetails);
      if(this.organizationDetails?.id){
        this.orgId = this.organizationDetails.id;
      }
      if (this.organizationDetails.themeConfig && this.organizationDetails.themeConfig != '') {
        this.themeData = this.organizationDetails.themeConfig;
      }
      if (this.organizationDetails.themeData && this.organizationDetails.themeData != '') {
        this.tenantAppTheme = this.organizationDetails.themeData;
      }

      if(!this.themeData?.logos?.logo5?.img){
        let logos = this.themeData?.logos || {};
        logos['logo5'] = { img: null };
        this.themeData.logos = logos;
      }

      // if(!this.themeData?.logos?.logo5?.img){
      //   this.themeData.logos.logo5 = this.defaultOrganizationDetail?.themeConfig?.logos?.logo5;
      // }

      this.loaded = true;
      console.log("Org ID received from api ===", this.orgId);

      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'this.tenantAppTheme', this.tenantAppTheme);
      
      this.appService.setThemeConfig(this.themeData);
      this.appService.setTenantAppThemeConfig(this.tenantAppTheme);
      this.appService.setOrgId(this.orgId);

      this.updateThemeVariables(this.themeData);
      this.appService.setThemeConfigLoading(false);
      this.appService.setTenantAppThemeConfigLoading(false);
    });

    this.setUpTitleAndMeta();

    this.setUpLanguage();

    this.setPwaServices();

    // If a previous session was restored from storage (page reload), the
    // loginSuccess effect won't fire — so hydrate the brand list here too,
    // and re-pull permissions in case a role changed while this session's
    // stored copy went stale (localStorage can be up to 7 days old).
    this.store.select(fromApp.selectAuthState).pipe(take(1)).subscribe((auth) => {
      if (auth?.isAuthenticated) {
        this.store.dispatch(BrandActions.loadBrands());
        this.store.dispatch(AuthActions.refreshPermissions());
      }
    });

    this.socketService.init();

    // Ensure AppAnalyticsService is instantiated and initialized
    this.appAnalyticsService.getServiceStatus();

    window.addEventListener("load", function () {
      setTimeout(() => {
        // Hide the address bar!
        window.scrollTo(0, 1);
      }, 0);
    });

    // this.showLoggingSetupButton = !env.production;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    // Check if Alt + m is pressed
    if (event.altKey && event.key === 'm') {
      // Wait for 'l' or 's' key pressthis.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Waiting for next seq key...');
      
      const waitForG = (e: KeyboardEvent) => {
        if (e.key === 'l') {
          this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Enabling Log setting...');
          this.showLoggingSetupButton = !this.showLoggingSetupButton; // Toggle the button visibility
          window.removeEventListener('keydown', waitForG); // Remove the listener
        } else if (e.key === 's') {
          this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Enabling Log setting...');
          this.showLoggingSetupButton = !this.showLoggingSetupButton; // Toggle the button visibility
          window.removeEventListener('keydown', waitForG); // Remove the listener
          this.openLoggingSettingsUiDialog();
        }
      };

      window.addEventListener('keydown', waitForG);
    }
  }

  // @HostListener("window:visibilitychange", ["$event"])
  // @HostListener('window:focus', ['$event'])
  // onFocus(event: any): void {
  //   this.socketService.verifyConnectionStatus();
  // }

  @HostListener('window:beforeinstallprompt', ['$event'])
  onBeforeInstallPrompt(event: Event) {
    event.preventDefault();
    this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'Before Install Prompt Event', event)
    this.pwaPromptInstallService.setDeferredPWAInstallPromptObs(event);
  }

  setUpTitleAndMeta() {
    const frontendAppVersion = env.appVersion;

    this.titleService.setTitle(`Hthai Clienteling (v${frontendAppVersion})`);
    this.metaService.addTag({ name: 'description', content: 'Hthai Clienteling Wep App' }, true);
    this.metaService.addTag({ name: 'keywords', content: 'Hthai Clienteling' }, true);
  }


  setUpLanguage() {
    this.translocoService.langChanges$.subscribe(lang => {
      if (this.canSaveLanguageChange) {
        this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'App Language Changed: Saving...', lang);
        // Do not modify, until first attempt to read from localStorage is made...
        storageHelper.setToLocalStorageWithExpiry('user_preferred_language', lang, 604800);
      }
    });

    // Check if user have selected different preferred language..
    let userLang = storageHelper.getFromLocalStorageWithExpiry('user_preferred_language');
    if (!userLang || userLang == '') {
      userLang = getBrowserLang();
    }

    this.translocoService.setActiveLang(userLang);  // Setting the default browser language..
    this.canSaveLanguageChange = true;
  }


  setPwaServices() {

    if (env.production || env.stage) {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, '[PWA]: Initializing PWA Services...');
      this.pwaCheckUpdateService.init();
      this.pwaLogUpdateService.init();
      this.pwaPromptUpdateService.init();
      this.pwaRecoverService.init();
    } else {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, '[PWA]: Not Initializing PWA Services...');
    }
  }


  openLoggingSettingsUiDialog() {
    const dialogRef = this.dialog.open(LoggingSetupDialogComponent, {
      minWidth: "365px",
      width: "60%",
      panelClass: ['tis-simple-confirmation'],
      data: {},
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      this.loggingService.log(LogLevel.DEBUG, AppComponent.COMPONENT_NAME, 'The LoggingSetupDialogComponent dialog was closed with result:', result);
    });
  }


  updateThemeVariables(themeConfig: any) {
    const root = document.documentElement;

    // Default colors
    root.style.setProperty('--tis-default-primary-shimmer-color', `#00000024`);
    // root.style.setProperty('--tis-default-primary-shimmer-color', `${themeConfig.bgAndFontColor.default.primary}79`);
    root.style.setProperty('--tis-default-primary-light-color', `${themeConfig.bgAndFontColor.default.primary}57`);
    root.style.setProperty('--tis-default-primary-color', themeConfig.bgAndFontColor.default.primary);
    root.style.setProperty('--tis-default-bg-color', themeConfig.bgAndFontColor.default.bg[0]);
    root.style.setProperty('--tis-default-text-color', themeConfig.bgAndFontColor.default.text);
    root.style.setProperty('--tis-default-icon-color', themeConfig.bgAndFontColor.default.icon);
    root.style.setProperty('--tis-default-heading-color', themeConfig.bgAndFontColor.default.heading);
    root.style.setProperty('--tis-default-font-family', themeConfig.bgAndFontColor.default.font);
    root.style.setProperty('--tis-default-btn-type', themeConfig.bgAndFontColor.default.btnType);
    root.style.setProperty('--tis-default-btn-border', themeConfig.bgAndFontColor.default.btnBorder);

    // Primary, Secondary, Info, Warning, Cancel, Success
    root.style.setProperty('--tis-primary-bg-color', themeConfig.bgAndFontColor.default?.primaryBtn?.bg[0]);
    root.style.setProperty('--tis-primary-text-color', themeConfig.bgAndFontColor.default?.primaryBtn?.text);
    root.style.setProperty('--tis-secondary-bg-color', themeConfig.bgAndFontColor.default?.secondaryBtn?.bg[0]);
    root.style.setProperty('--tis-secondary-text-color', themeConfig.bgAndFontColor.default?.secondaryBtn?.text);
    root.style.setProperty('--tis-info-bg-color', themeConfig.bgAndFontColor.default?.infoBtn?.bg[0]);
    root.style.setProperty('--tis-info-text-color', themeConfig.bgAndFontColor.default?.infoBtn?.text);
    root.style.setProperty('--tis-warning-bg-color', themeConfig.bgAndFontColor.default?.warningBtn?.bg[0]);
    root.style.setProperty('--tis-warning-text-color', themeConfig.bgAndFontColor.default?.warningBtn?.text);
    root.style.setProperty('--tis-cancel-bg-color', themeConfig.bgAndFontColor.default?.cancelBtn?.bg[0]);
    root.style.setProperty('--tis-cancel-text-color', themeConfig.bgAndFontColor.default?.cancelBtn?.text);
    root.style.setProperty('--tis-success-bg-color', themeConfig.bgAndFontColor.default?.successBtn?.bg[0]);
    root.style.setProperty('--tis-success-text-color', themeConfig.bgAndFontColor.default?.successBtn?.text);

    // Sidebar
    root.style.setProperty('--tis-sidebar-menu-bg-color', themeConfig.bgAndFontColor.sidebar.bg[0]);
    root.style.setProperty('--tis-sidebar-menu-text-color', themeConfig.bgAndFontColor.sidebar.text);
    root.style.setProperty('--tis-sidebar-menu-active-bg-color', themeConfig.bgAndFontColor.sidebar.activeBg[0]);
    root.style.setProperty('--tis-sidebar-menu-font-weight', this.setBold(themeConfig.bgAndFontColor.sidebar.bold));
    root.style.setProperty('--tis-sidebar-menu-font-family', themeConfig.bgAndFontColor.sidebar.font);

    // Dashboard
    root.style.setProperty('--tis-dashboard-bg-color', themeConfig.bgAndFontColor.dashboard.bg[0]);
    root.style.setProperty('--tis-dashboard-text-color', themeConfig.bgAndFontColor.dashboard.text);
    root.style.setProperty('--tis-dashboard-font-weight', this.setBold(themeConfig.bgAndFontColor.dashboard.bold));
    root.style.setProperty('--tis-dashboard-font-family', themeConfig.bgAndFontColor.dashboard.font);

    // Account
    root.style.setProperty('--tis-account-bg-color', themeConfig.bgAndFontColor.account.bg[0]);
    root.style.setProperty('--tis-account-text-color', themeConfig.bgAndFontColor.account.text);
    root.style.setProperty('--tis-account-font-weight', this.setBold(themeConfig.bgAndFontColor.account.bold));
    root.style.setProperty('--tis-account-font-family', themeConfig.bgAndFontColor.account.font);

    // Header
    root.style.setProperty('--tis-header-bg-color', themeConfig.bgAndFontColor.header.bg[0]);
    root.style.setProperty('--tis-header-bg-color-1', themeConfig.bgAndFontColor.header.bg[0]);
    root.style.setProperty('--tis-header-bg-color-2', themeConfig.bgAndFontColor.header.bg[1]);
    root.style.setProperty('--tis-header-text-color', themeConfig.bgAndFontColor.header.text);
    root.style.setProperty('--tis-header-font-weight', this.setBold(themeConfig.bgAndFontColor.header.bold));
    root.style.setProperty('--tis-header-font-family', themeConfig.bgAndFontColor.header.font);

    // Notification
    root.style.setProperty('--tis-notification-bg-color', themeConfig.bgAndFontColor.notification.bg[0]);
    root.style.setProperty('--tis-notification-text-color', themeConfig.bgAndFontColor.notification.text);
    root.style.setProperty('--tis-notification-heading-color', themeConfig.bgAndFontColor.notification.heading);
    root.style.setProperty('--tis-notification-heading-font-weight', this.setBold(themeConfig.bgAndFontColor.notification.headingBold));
    root.style.setProperty('--tis-notification-font-weight', this.setBold(themeConfig.bgAndFontColor.notification.bold));
    root.style.setProperty('--tis-notification-font-family', themeConfig.bgAndFontColor.notification.font);
  }

  setBold(bold: any){
    if(bold){
      return `${bold}`;
    }
    else{
      return '';
    }
  }

}
